import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Table } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import { useArchiveWebsiteLeadMutation, useGetWebsiteLeadCountQuery, useGetWebsiteLeadsQuery, useMoveLeadToPriorityEntryMutation, useRestoreWebsiteLeadMutation } from "../../graphql/graphql";
import { doesArrayHaveData } from "../../utils/Array.utils";
import { calculateOffset } from "../../utils/PageIndex.utils";
import { WebsiteLeadEmptyState } from "./WebsiteLeadEmptyState";
import { AutoHeightContainer } from "../../shared/Layout/AutoHeightContainer";
import { Confirmation } from "../../shared/Confirmation";
import { TablePagination } from "../../shared/TablePagination";
import { WebsiteLeadRow } from "./WebsiteLeadRow";
import { ManagedTable } from "../../shared/ManagedTable";
import { useTherapproveDrawers } from "../../shared/TherapproveOverlays";
export const WebsiteLeadView = ({ search = "", isActiveTab, archived = false, therapyCategoryIds, onOpenCodeModal }) => {
    // State
    const [page, setPage] = useState(1);
    const { patient } = useTherapproveDrawers();
    // Effects
    useEffect(() => {
        setPage(1);
    }, [search]);
    const [confirmationProps, setConfirmationProps] = useState(null);
    // Mutations
    const [{ fetching: moveWebsiteLeadToPriorityEntryFetching }, moveWebsiteLeadToPriorityEntryMutation] = useMoveLeadToPriorityEntryMutation();
    const [{ fetching: restoreWebsiteLeadFetching }, restoreWebsiteLeadMutation] = useRestoreWebsiteLeadMutation();
    const [{ fetching: archiveWebsiteLeadFetching }, archiveWebsiteLeadMutation] = useArchiveWebsiteLeadMutation();
    // Get the website leads.
    const [{ data, fetching }] = useGetWebsiteLeadsQuery({
        requestPolicy: "cache-and-network",
        variables: {
            archived,
            search,
            offset: calculateOffset(page),
            therapyCategoryIds: therapyCategoryIds?.length ? therapyCategoryIds : undefined
        },
        pause: isActiveTab === false
    });
    const websiteLeads = data?.websiteLeads?.websiteLeads ?? [];
    // Get the website lead count.
    const [{ data: countData }] = useGetWebsiteLeadCountQuery({
        variables: {
            archived,
            therapyCategoryIds: therapyCategoryIds?.length ? therapyCategoryIds : undefined
        },
        pause: isActiveTab === false
    });
    const count = countData?.websiteLeads?.websiteLeadCount ?? 0;
    // Confirmation Modal
    const [confirmationModalOpened, { open: openConfirmationModal, close: closeConfirmationModal }] = useDisclosure(false);
    // Handlers
    const openPatientProfileDrawer = (patientId) => {
        patient.open({ patientId });
    };
    const moveWebsiteLeadToPriorityEntry = (websiteLeadId) => {
        setConfirmationProps({
            action: "move",
            handleAction: async () => {
                await moveWebsiteLeadToPriorityEntryMutation({ websiteLeadId });
                closeConfirmationModal();
            }
        });
        openConfirmationModal();
    };
    const archiveRestoreWebsiteLead = (websiteLeadId) => {
        if (!!archived) {
            setConfirmationProps({
                action: "restore",
                handleAction: async () => {
                    await restoreWebsiteLeadMutation({ websiteLeadId });
                    closeConfirmationModal();
                }
            });
        }
        else {
            setConfirmationProps({
                action: "archive",
                handleAction: async () => {
                    await archiveWebsiteLeadMutation({ websiteLeadId });
                    closeConfirmationModal();
                }
            });
        }
        openConfirmationModal();
    };
    /**
     * Create the table header
     */
    const tableHead = (_jsxs(Table.Tr, { children: [_jsx(Table.Th, { style: { paddingLeft: "0.5rem" }, children: "Primary Contact" }), _jsx(Table.Th, { children: "Contact Info" }), _jsx(Table.Th, { children: "Client" }), _jsx(Table.Th, { children: "Therapy Needed" }), _jsx(Table.Th, { children: "Location" }), _jsx(Table.Th, { children: "Wait Time" }), _jsx(Table.Th, {})] }));
    // Website Lead Rows
    const websiteLeadRows = useMemo(() => {
        if (!doesArrayHaveData(websiteLeads))
            return [];
        return websiteLeads.map((websiteLead) => {
            return (_jsx(WebsiteLeadRow, { websiteLead: websiteLead, moveWebsiteLeadToPriorityEntry: moveWebsiteLeadToPriorityEntry, archiveRestoreWebsiteLead: archiveRestoreWebsiteLead, modalHandlers: {
                    patientProfileDrawerOpen: openPatientProfileDrawer
                } }, websiteLead?.websiteLeadId));
        });
    }, [websiteLeads]);
    const caption = _jsx(TablePagination, { count: count, page: page, setPage: setPage });
    return (_jsxs(AutoHeightContainer, { children: [_jsx(ManagedTable, { withBorder: true, header: tableHead, rows: websiteLeadRows, caption: caption, minWidth: 800, fetching: fetching, emptyState: _jsx(WebsiteLeadEmptyState, { onAddCode: onOpenCodeModal }) }), confirmationProps && (_jsx(Confirmation, { opened: confirmationModalOpened, close: closeConfirmationModal, action: confirmationProps.action, handleAction: confirmationProps.handleAction, isLoading: moveWebsiteLeadToPriorityEntryFetching ||
                    restoreWebsiteLeadFetching ||
                    archiveWebsiteLeadFetching }))] }));
};
